import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { musicDB } from "../../resources/musicData";
import "./lyrics.css";

const isLyricsSyncToolEnabled = () => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("syncLyrics") === "1" || params.get("sync") === "1";
};

const isTypingInInput = () => {
  if (typeof document === "undefined") return false;
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  return tag === "input" || tag === "textarea" || el.isContentEditable;
};

const formatLyricsForMusicData = (lyrics) => {
  const lines = lyrics.map((line) => {
    const time =
      typeof line.time === "number" && Number.isFinite(line.time)
        ? line.time.toFixed(2)
        : "0.00";
    return `  { time: ${time}, text: ${JSON.stringify(line.text)} },`;
  });

  return `lyrics: [\n${lines.join("\n")}\n],`;
};

const Lyrics = (props) => {
  const { trackId, currentTime, onLastLyricReached } = props;
  const containerRef = useRef(null);
  const lineRefs = useRef({});
  const syncToolEnabled = useMemo(() => isLyricsSyncToolEnabled(), []);
  const [syncMode, setSyncMode] = useState(syncToolEnabled);
  const [draftLyrics, setDraftLyrics] = useState([]);
  const [armedIndex, setArmedIndex] = useState(0);
  const [toast, setToast] = useState(null);

  const track =
    trackId !== undefined
      ? musicDB.find((track) => track.id === trackId)
      : null;

  const baseLyrics = useMemo(() => track?.lyrics || [], [track]);
  const storageKey = trackId ? `lyricsSync:${trackId}` : null;

  useEffect(() => {
    if (!trackId) return;
    if (!syncToolEnabled) {
      setDraftLyrics(baseLyrics);
      setArmedIndex(0);
      return;
    }

    const stored = storageKey ? localStorage.getItem(storageKey) : null;
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setDraftLyrics(parsed);
          setArmedIndex(0);
          return;
        }
      } catch {
        // Fall back to base lyrics
      }
    }

    setDraftLyrics(baseLyrics);
    setArmedIndex(0);
  }, [baseLyrics, storageKey, syncToolEnabled, trackId]);

  const lyrics =
    syncToolEnabled && Array.isArray(draftLyrics) && draftLyrics.length > 0
      ? draftLyrics
      : baseLyrics;

  // Find the current line of lyrics based on the currentTime
  const currentLineIndex = lyrics.findIndex((line, index) => {
    const nextLineTime = lyrics[index + 1]
      ? lyrics[index + 1].time
      : Number.MAX_VALUE;
    return line.time <= currentTime && currentTime < nextLineTime;
  });

  // Check if we've reached or passed the last lyric
  // Must have lyrics, song must be playing, and we must actually be at/past the last lyric
  const lastLyricTime = lyrics.length > 0 ? lyrics[lyrics.length - 1].time : 0;
  const hasReachedLastLyric =
    lyrics.length > 0 &&
    lastLyricTime > 0 &&
    currentTime > 0 &&
    (currentLineIndex >= lyrics.length - 1 || currentTime >= lastLyricTime);

  // Notify parent when last lyric is reached
  useEffect(() => {
    if (onLastLyricReached && hasReachedLastLyric) {
      onLastLyricReached(true);
    } else if (onLastLyricReached && !hasReachedLastLyric) {
      onLastLyricReached(false);
    }
  }, [hasReachedLastLyric, onLastLyricReached]);

  // Auto-scroll: follow active line in playback, or armed line in sync mode
  useEffect(() => {
    const targetIndex = syncMode ? armedIndex : currentLineIndex;
    if (targetIndex !== -1 && lineRefs.current[targetIndex]) {
      const activeLine = lineRefs.current[targetIndex];
      const container = containerRef.current;

      if (container && activeLine) {
        const lineTop = activeLine.offsetTop;
        // Position active line about 20% from top of container
        const topOffset = container.clientHeight * 0.2;
        const scrollPosition = lineTop - topOffset;

        container.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: "smooth",
        });
      }
    }
  }, [armedIndex, currentLineIndex, syncMode]);

  const persistDraft = useCallback(
    (updatedLyrics) => {
      if (!syncToolEnabled || !storageKey) return;
      localStorage.setItem(storageKey, JSON.stringify(updatedLyrics));
    },
    [storageKey, syncToolEnabled]
  );

  const setLineTime = useCallback(
    (index, time) => {
      if (!syncToolEnabled || !syncMode) return;

      setDraftLyrics((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        const safeIndex = Math.max(0, Math.min(index, prev.length - 1));
        const updatedLyrics = prev.map((line, i) =>
          i === safeIndex ? { ...line, time } : line
        );

        persistDraft(updatedLyrics);
        setArmedIndex(Math.min(safeIndex + 1, updatedLyrics.length - 1));

        console.log(`\n=== LYRIC SYNC ===`);
        console.log(`Line ${safeIndex + 1}/${updatedLyrics.length}`);
        console.log(`"${updatedLyrics[safeIndex].text}"`);
        console.log(`time: ${time.toFixed(2)},`);
        console.log(`=================\n`);

        return updatedLyrics;
      });
    },
    [persistDraft, syncMode, syncToolEnabled]
  );

  const showToast = useCallback((message) => {
    setToast(message);
    setTimeout(() => setToast(null), 1600);
  }, []);

  const handleCopy = useCallback(async () => {
    if (!syncToolEnabled) return;
    const text = formatLyricsForMusicData(lyrics);
    try {
      await navigator.clipboard.writeText(text);
      showToast("Copied lyrics to clipboard");
    } catch (err) {
      console.error("Clipboard copy failed:", err);
      showToast("Copy failed (see console)");
    }
  }, [lyrics, showToast, syncToolEnabled]);

  const handleReset = useCallback(() => {
    if (!syncToolEnabled) return;
    if (storageKey) localStorage.removeItem(storageKey);
    setDraftLyrics(baseLyrics);
    setArmedIndex(0);
    showToast("Reset draft sync data");
  }, [baseLyrics, showToast, storageKey, syncToolEnabled]);

  useEffect(() => {
    if (!syncToolEnabled || !syncMode) return;
    if (!trackId) return;

    const onKeyDown = (e) => {
      if (isTypingInInput()) return;
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        setLineTime(armedIndex, currentTime);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setArmedIndex((idx) =>
          Math.min(idx + 1, Math.max(0, lyrics.length - 1))
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setArmedIndex((idx) => Math.max(0, idx - 1));
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [armedIndex, currentTime, lyrics.length, setLineTime, syncMode, syncToolEnabled, trackId]);

  if (!trackId) {
    return (
      <div className="lyrics-container idle" ref={containerRef}>
        <div className="idle-message">Press Play to See Lyrics</div>
      </div>
    );
  }

  if (!track || lyrics.length === 0) {
    return <div className="lyrics-container">Lyrics Not Found</div>;
  }

  return (
    <div className="lyrics-container" ref={containerRef}>
      {syncToolEnabled && (
        <div className="lyrics-sync-toolbar">
          <label className="lyrics-sync-toggle">
            <input
              type="checkbox"
              checked={syncMode}
              onChange={(e) => setSyncMode(e.target.checked)}
            />
            Sync mode
          </label>
          <div className="lyrics-sync-meta">
            <span className="lyrics-sync-meta-item">
              t={currentTime.toFixed(2)}
            </span>
            <span className="lyrics-sync-meta-item">
              next {Math.min(armedIndex + 1, lyrics.length)}/{lyrics.length}
            </span>
            <span className="lyrics-sync-meta-item">
              {syncMode ? "Click a line or press Space/Enter" : "Sync paused"}
            </span>
          </div>
          <button
            className="lyrics-sync-button"
            type="button"
            onClick={handleCopy}
          >
            Copy
          </button>
          <button
            className="lyrics-sync-button"
            type="button"
            onClick={handleReset}
          >
            Reset
          </button>
          {toast && <div className="lyrics-sync-toast">{toast}</div>}
        </div>
      )}
      {lyrics.map((line, index) => (
        <div
          key={index}
          ref={(el) => (lineRefs.current[index] = el)}
          className={`lyrics-line ${
            index === currentLineIndex
              ? "highlighted"
              : index < currentLineIndex
              ? "past"
              : "future"
          } ${syncToolEnabled && syncMode && index === armedIndex ? "sync-armed" : ""}`}
          onClick={() => setLineTime(index, currentTime)}
          style={{ cursor: syncToolEnabled && syncMode ? "pointer" : "default" }}
          title={
            syncToolEnabled && syncMode
              ? "Click to set IN point (or press Space/Enter)"
              : undefined
          }
        >
          {line.text}
        </div>
      ))}
    </div>
  );
};

export default Lyrics;
